import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    Select,
    MenuItem,
    Box,
    FormControlLabel,
    Typography,
    Checkbox,
    IconButton,
    useMediaQuery,
    SxProps,
    Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IRoleCreator } from '../../../../types/global/helper';
import { supportedCurrency } from '../../../../constants/currency';
import { CurrencyTextInput } from '../../../CurrencyTextInput/CurrencyTextInput';
import { RichTextEditor } from '../../../Common/RichTextEditor';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';

/** Form state allows empty string while typing ages (controlled number inputs otherwise snap back). */
type RoleDialogForm = Omit<Partial<IRoleCreator>, 'min_age' | 'max_age'> & {
    min_age?: number | string;
    max_age?: number | string;
};

interface RoleManagementDialogProps {
    open: boolean;
    role?: IRoleCreator;
    onClose: () => void;
    onSave: (role: Partial<IRoleCreator>) => void;
    /** Merged into `Dialog` `PaperProps.sx` (e.g. brand obsidian shell). */
    paperSx?: SxProps<Theme>;
}

const CREATE_ROLE_DEFAULTS: RoleDialogForm = {
    character: '',
    description: '',
    gender: 'male',
    min_age: 6,
    max_age: 70,
    compensation: 0,
    currency: 'USD',
    role_type: { label: 'Cast', value: 'cast' },
    expertise: 'beginner',
    required_media: [],
    requires_subscription: false,
    nudity_or_kissing: false,
};

/** Plain text from rich HTML (Quill empty state is often `<p><br></p>`). */
function plainTextFromRichHtml(html: string): string {
    if (!html?.trim()) return '';
    if (typeof document !== 'undefined') {
        const div = document.createElement('div');
        div.innerHTML = html;
        return (div.textContent || div.innerText || '').replace(/\u200B/g, '').trim();
    }
    return html
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export const RoleManagementDialog: React.FC<RoleManagementDialogProps> = ({ open, role, onClose, onSave, paperSx }) => {
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:768px)');
    const fs = theme[themeStyle as keyof typeof theme];
    const fieldBg = BRAND_SURFACE_CONTAINER_HIGH;
    const ageNumberTextFieldSx: SxProps<Theme> = {
        '& .MuiOutlinedInput-root': {
            color: fs.primary_white,
            backgroundColor: fieldBg,
        },
        '& input[type=number]': {
            MozAppearance: 'textfield',
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
    };
    const labelSx = {
        fontFamily: FONT_ENUM.MAIN_BOLD,
        color: 'rgba(255,255,255,0.65)',
        mb: 1,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.1em',
        fontSize: 11,
    };
    const [formData, setFormData] = useState<RoleDialogForm>(() => ({ ...CREATE_ROLE_DEFAULTS }));

    /**
     * When the dialog opens, seed from `role` for edit or defaults for create.
     * (Avoid clearing form in an effect cleanup when `role` goes undefined — that
     * left state as `{}` and kept Save disabled.)
     */
    useEffect(() => {
        if (!open) return;
        if (role) {
            setFormData({
                ...CREATE_ROLE_DEFAULTS,
                ...role,
                character: role.character || '',
                description: role.description ?? '',
                gender:
                    typeof role.gender === 'string'
                        ? role.gender.toLowerCase()
                        : String(role.gender || 'male').toLowerCase(),
                min_age: role.min_age ?? 6,
                max_age: role.max_age ?? 70,
                compensation: role.compensation ?? 0,
                currency: role.currency || 'USD',
                role_type: role.role_type || { label: 'Cast', value: 'cast' },
                required_media: role.required_media || [],
                requires_subscription: role.requires_subscription ?? false,
                nudity_or_kissing: role.nudity_or_kissing ?? false,
                expertise: role.expertise || 'beginner',
            });
        } else {
            setFormData({ ...CREATE_ROLE_DEFAULTS });
        }
    }, [open, role]);

    const handleInputChange = (field: keyof IRoleCreator, value: any) => {
        setFormData((prev: RoleDialogForm) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        if (!canSave) return;
        const min = Math.max(0, Number(formData.min_age));
        const max = Math.max(min, Number(formData.max_age));
        onSave({ ...formData, min_age: min, max_age: max });
        onClose();
    };

    const roleTypeValue =
        typeof formData.role_type === 'object'
            ? String(formData.role_type?.value || '').toLowerCase()
            : String(formData.role_type || '').toLowerCase();
    const descriptionText = plainTextFromRichHtml(String(formData.description || ''));
    const hasCharacter = String(formData.character || '').trim().length > 0;
    const hasDescription = descriptionText.length > 0;
    const hasGender = String(formData.gender || '').trim().length > 0;
    const hasExpertise = String(formData.expertise || '').trim().length > 0;
    const hasRoleType = roleTypeValue.length > 0;
    const minAge = formData.min_age === '' || formData.min_age === undefined ? NaN : Number(formData.min_age);
    const maxAge = formData.max_age === '' || formData.max_age === undefined ? NaN : Number(formData.max_age);
    const validAgeRange =
        Number.isFinite(minAge) && Number.isFinite(maxAge) && minAge >= 0 && maxAge >= 0 && maxAge >= minAge;
    const compensation = Number(formData.compensation ?? 0);
    const hasCompensation = Number.isFinite(compensation) && compensation >= 0;
    const hasCurrency = String(formData.currency || '').trim().length > 0;
    const canSave =
        hasCharacter &&
        hasDescription &&
        hasGender &&
        hasExpertise &&
        hasRoleType &&
        validAgeRange &&
        hasCompensation &&
        hasCurrency;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            slotProps={{
                backdrop: {
                    sx: { backgroundColor: 'rgba(6, 6, 6, 0.78)' },
                },
            }}
            PaperProps={{
                sx: {
                    backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                    color: fs.primary_white,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}55`,
                    borderRadius: isMobile ? 0 : '16px',
                    boxShadow: '0px 18px 46px rgba(0, 0, 0, 0.6)',
                    ...(isMobile && {
                        height: '100dvh',
                        maxHeight: '100dvh',
                        margin: 0,
                        borderRadius: 0,
                    }),
                    ...(paperSx as object),
                },
            }}>
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: { xs: '1.35rem', sm: '1.5rem' },
                    color: fs.primary_white,
                    py: 2,
                    px: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                    backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                }}>
                <Box sx={{ width: 40, height: 40 }} />
                <Typography
                    component="span"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: '1.35rem', sm: '1.5rem' },
                        lineHeight: 1.1,
                        textAlign: 'center',
                        flex: 1,
                        color: fs.primary_white,
                    }}>
                    {role ? 'Edit role' : 'Create new role'}
                </Typography>
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        color: 'rgba(255,255,255,0.55)',
                        '&:hover': {
                            color: BRAND_LUXE_PRIMARY,
                            bgcolor: 'rgba(255,255,255,0.06)',
                        },
                    }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    borderColor: `${BRAND_OUTLINE_VARIANT} !important`,
                    backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                    px: { xs: 2, sm: 2.5 },
                    py: 2,
                    ...(isMobile && {
                        height: 'calc(100dvh - 160px)',
                        overflowY: 'auto',
                    }),
                    '& .MuiInputLabel-root': {
                        color: 'rgba(255,255,255,0.65)',
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: fs.gray_text,
                        color: fs.primary_white,
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                        },
                        '& input, & textarea': {
                            color: fs.primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 16,
                        },
                        '& input::placeholder, & textarea::placeholder': {
                            color: 'rgba(255,255,255,0.48)',
                            opacity: 1,
                        },
                    },
                    '& .MuiSelect-select': {
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 16,
                    },
                }}>
                <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Character Name"
                            value={formData.character}
                            onChange={e => handleInputChange('character', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: fs.primary_white,
                                    backgroundColor: fieldBg,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography sx={labelSx}>Character Description</Typography>
                        <RichTextEditor
                            value={formData.description ?? ''}
                            onChange={v => handleInputChange('description', v)}
                            shellVariant="brandDashboard"
                            placeholder="Describe the ideal candidate..."
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={labelSx}>Gender</Typography>
                        <FormControl fullWidth>
                            <Select
                                value={
                                    typeof formData.gender === 'string'
                                        ? formData.gender.toLowerCase()
                                        : formData.gender || 'male'
                                }
                                onChange={e => handleInputChange('gender', e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: fieldBg,
                                            color: fs.primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: fs.primary_white,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: BRAND_LUXE_PRIMARY,
                                                    color: '#0a1211',
                                                    '&:hover': {
                                                        backgroundColor: BRAND_LUXE_PRIMARY,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        color: fs.primary_white,
                                        bgcolor: fieldBg,
                                        borderRadius: 2,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: fs.primary_white,
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                                    },
                                }}>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="any">Any</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={labelSx}>Min Age</Typography>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            inputProps={{ min: 0, step: 1 }}
                            value={formData.min_age === '' || formData.min_age === undefined ? '' : formData.min_age}
                            onChange={e => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    handleInputChange('min_age', '');
                                    return;
                                }
                                if (!/^\d+$/.test(raw)) return;
                                const n = parseInt(raw, 10);
                                if (!Number.isNaN(n)) handleInputChange('min_age', Math.max(0, n));
                            }}
                            sx={ageNumberTextFieldSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={labelSx}>Max Age</Typography>
                        <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            inputProps={{ min: 0, step: 1 }}
                            value={formData.max_age === '' || formData.max_age === undefined ? '' : formData.max_age}
                            onChange={e => {
                                const raw = e.target.value;
                                if (raw === '') {
                                    handleInputChange('max_age', '');
                                    return;
                                }
                                if (!/^\d+$/.test(raw)) return;
                                const n = parseInt(raw, 10);
                                if (!Number.isNaN(n)) handleInputChange('max_age', Math.max(0, n));
                            }}
                            sx={ageNumberTextFieldSx}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <CurrencyTextInput
                                value={formData.compensation !== null ? Number(formData.compensation) : 0}
                                minAmount={0}
                                details={0}
                                supportedCurrency={supportedCurrency}
                                initialCurrency={formData.currency}
                                initialValue={Number(formData.compensation)}
                                message={''}
                                brandChrome
                                onChange={({ currency, value }) => {
                                    handleInputChange('currency', currency);
                                    handleInputChange('compensation', value);
                                }}
                                sx={{
                                    color: fs.primary_white,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: fieldBg,
                                    },
                                    '& .MuiInputAdornment-root': {
                                        backgroundColor: fieldBg,
                                        borderRadius: 1,
                                    },
                                    '& .MuiInputAdornment-root .MuiInputBase-root': {
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiInputAdornment-root .MuiSelect-select': {
                                        backgroundColor: 'transparent',
                                        color: fs.primary_white,
                                    },
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelSx}>Expertise Level</Typography>
                        <FormControl fullWidth>
                            <Select
                                value={formData.expertise || 'beginner'}
                                onChange={e => handleInputChange('expertise', e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: fieldBg,
                                            color: fs.primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: fs.primary_white,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: BRAND_LUXE_PRIMARY,
                                                    color: '#0a1211',
                                                    '&:hover': {
                                                        backgroundColor: BRAND_LUXE_PRIMARY,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        color: fs.primary_white,
                                        bgcolor: fieldBg,
                                        borderRadius: 2,
                                    },

                                    '& .MuiSvgIcon-root': {
                                        color: fs.primary_white,
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                                    },
                                }}>
                                <MenuItem value="beginner">Beginner</MenuItem>
                                <MenuItem value="mid-level">Mid Level</MenuItem>
                                <MenuItem value="expert">Expert</MenuItem>
                                <MenuItem value="professional">Professional</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={labelSx}>Role Type</Typography>
                        <FormControl fullWidth>
                            <Select
                                value={
                                    typeof formData.role_type === 'object'
                                        ? String(formData.role_type.value || 'cast').toLowerCase()
                                        : 'cast'
                                }
                                onChange={e =>
                                    handleInputChange('role_type', {
                                        label:
                                            String(e.target.value).charAt(0).toUpperCase() +
                                            String(e.target.value).slice(1),
                                        value: e.target.value,
                                    })
                                }
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: fieldBg,
                                            color: fs.primary_white,
                                            '& .MuiMenuItem-root': {
                                                color: fs.primary_white,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: BRAND_LUXE_PRIMARY,
                                                    color: '#0a1211',
                                                    '&:hover': {
                                                        backgroundColor: BRAND_LUXE_PRIMARY,
                                                    },
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiSelect-select': {
                                        color: fs.primary_white,
                                        bgcolor: fieldBg,
                                        borderRadius: 2,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: fs.primary_white,
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: '0 0 0 1px rgba(236,170,0,0.35)',
                                    },
                                }}>
                                <MenuItem value="cast">Cast</MenuItem>
                                <MenuItem value="crew">Crew</MenuItem>
                                <MenuItem value="technical">Technical</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <FormControlLabel
                                title={'Requires Subscription'}
                                control={
                                    <Checkbox
                                        checked={(formData['requires_subscription'] as unknown as boolean) || false}
                                        onChange={() => {
                                            handleInputChange(
                                                'requires_subscription',
                                                !formData['requires_subscription'],
                                            );
                                        }}
                                        sx={{
                                            color: 'rgba(255,255,255,0.45)',
                                            '&.Mui-checked': {
                                                color: BRAND_LUXE_PRIMARY,
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: BRAND_LUXE_PRIMARY,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                        Verified Thspians only
                                    </Typography>
                                } // Label changes based on state
                            />

                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                    mt: 1,
                                }}>
                                Enabling this option restricts applications for this role to verified Thspians only.
                            </Typography>
                        </FormControl>
                    </Grid>

                    {/* Nudity or Kissing - Only show for cast roles */}
                    {roleTypeValue === 'cast' && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <FormControlLabel
                                    title={'Nudity or Kissing'}
                                    control={
                                        <Checkbox
                                            checked={(formData['nudity_or_kissing'] as unknown as boolean) || false}
                                            onChange={() => {
                                                handleInputChange('nudity_or_kissing', !formData['nudity_or_kissing']);
                                            }}
                                            sx={{
                                                color: 'rgba(255,255,255,0.45)',
                                                '&.Mui-checked': {
                                                    color: BRAND_LUXE_PRIMARY,
                                                },
                                                '&.MuiCheckbox-indeterminate': {
                                                    color: BRAND_LUXE_PRIMARY,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                            This role involves nudity or kissing scenes
                                        </Typography>
                                    }
                                />

                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                        mt: 1,
                                    }}>
                                    Check this box if the role involves nudity or kissing scenes. This helps talent make
                                    informed decisions about applying.
                                </Typography>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2.5,
                    borderTop: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                    backgroundColor: BRAND_SURFACE_CONTAINER_LOW,
                    flexShrink: 0,
                }}>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!canSave}
                    disableElevation
                    sx={{
                        width: '100%',
                        borderRadius: 999,
                        minHeight: 56,
                        px: 3,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        border: 'none',
                        opacity: 1,
                        bgcolor: '#FFB82E',
                        color: '#0a0a0a',
                        boxShadow: '0 6px 22px rgba(0, 0, 0, 0.45)',
                        '&:hover': {
                            bgcolor: '#ffc85a',
                            color: '#0a0a0a',
                            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.5)',
                        },
                        '&.Mui-disabled': {
                            bgcolor: '#7a7a7a',
                            color: 'rgba(255,255,255,0.55)',
                            boxShadow: 'none',
                            opacity: 1,
                        },
                    }}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
